/**
 * The one line that tells a doctor which visit they have open (#19040).
 *
 * <p>The exam screen showed the patient's identity and a time labelled "Ngày khám" and nothing
 * else — not the room, not the queue number, not the khoa. The reception screen beside it has
 * columns for "Bác sĩ" and "Phòng · STT", and the "Lịch sử khám từ HIS" panel on this very screen
 * shows the room for every PAST visit. Only the visit in progress was anonymous.
 *
 * <p>With several rooms and several doctors there was nothing to reveal that the visit just opened
 * belongs to someone else's room; signing it records the wrong person in charge.
 *
 * <p>A pure function so the "never render the string `undefined`" rule (#18897) is testable
 * directly rather than inferred from a screenshot. Every part is dropped when absent, and the room
 * says "Chưa xếp phòng" rather than going silently missing — a blank there reads as "no room
 * needed", which is a different and wrong statement.
 */

export interface EncounterContextInput {
  roomName?: string | null;
  roomNo?: number | null;
  registrationNo?: number | null;
  departmentName?: string | null;
}

export function encounterContextParts(e: EncounterContextInput): string[] {
  const parts: string[] = [];

  parts.push(e.roomName?.trim() ? e.roomName.trim() : "Chưa xếp phòng");

  // Two different sequences. Under `queueScope: ROOM` each room counts on its own, so the number
  // the doctor calls out is roomNo; registrationNo is the reception number for the whole day.
  // Labelling either one just "STT" is how the wrong patient gets called.
  if (e.roomNo != null) parts.push(`STT vào phòng ${e.roomNo}`);
  if (e.registrationNo != null) parts.push(`STT quầy ${e.registrationNo}`);
  if (e.departmentName?.trim()) parts.push(`Khoa ${e.departmentName.trim()}`);

  return parts;
}

/** `"HH:mm"` of an ISO instant, or null — never the string `"Invalid Date"`. */
export function timeOnly(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * "Tiếp đón 15:12 · Vào khám 15:20" — two different moments the old screen collapsed into one.
 *
 * <p>The header used to print `startedAt` under the label "Ngày khám". `startedAt` is when the
 * EXAM began; the appointment's own time is when the patient was received. Showing one and naming
 * it the other is not a missing field, it is a wrong one.
 */
export function encounterTimingLabel(
  appointmentStartAt?: string | null,
  startedAt?: string | null
): string | null {
  const received = timeOnly(appointmentStartAt);
  const began = timeOnly(startedAt);
  if (received && began) return `Tiếp đón ${received} · Vào khám ${began}`;
  if (began) return `Vào khám ${began}`;
  if (received) return `Tiếp đón ${received}`;
  return null;
}
